import React from 'react';
import { useProject } from '../context/ProjectContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building, MapPin, User, LayoutGrid, Users } from 'lucide-react';

const ProjectSettings = () => {
  const { currentProject, updateProject, customers } = useProject();

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <p className="text-gray-400">Kein Projekt ausgewählt</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    updateProject(currentProject.id, { [field]: value });
  };

  // Finde zugeordneten Kunden und Gebäude
  const assignedCustomer = customers?.find(c => c.id === currentProject.customerId);
  const assignedBuilding = assignedCustomer?.buildings?.find(b => b.id === currentProject.buildingId);

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        
        {/* Kunden-Zuordnung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Kunden-Zuordnung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kunde</Label>
                <Select
                  value={currentProject.customerId || 'none'}
                  onValueChange={(value) => {
                    handleChange('customerId', value === 'none' ? '' : value);
                    // Setze Gebäude zurück wenn Kunde wechselt
                    handleChange('buildingId', '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">- Kein Kunde -</SelectItem>
                    {customers?.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gebäude</Label>
                <Select
                  value={currentProject.buildingId || 'none'}
                  onValueChange={(value) => handleChange('buildingId', value === 'none' ? '' : value)}
                  disabled={!currentProject.customerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Gebäude auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">- Kein Gebäude -</SelectItem>
                    {assignedCustomer?.buildings?.map((building) => (
                      <SelectItem key={building.id} value={building.id}>
                        {building.name} ({building.address})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Info-Anzeige wenn zugeordnet */}
            {assignedCustomer && (
              <div className="mt-2 p-3 bg-emerald-50 rounded-lg text-sm">
                <div className="font-medium text-emerald-700">{assignedCustomer.name}</div>
                {assignedBuilding && (
                  <div className="text-emerald-600 mt-1">
                    📍 {assignedBuilding.name} - {assignedBuilding.address}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Projektdaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Projektname</Label>
              <Input
                value={currentProject.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Projektname eingeben"
              />
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Textarea
                  value={currentProject.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Projektadresse eingeben"
                  className="pl-10"
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zeichnungsnummer</Label>
                <Input
                  value={currentProject.zeichnungsnummer || ''}
                  onChange={(e) => handleChange('zeichnungsnummer', e.target.value)}
                  placeholder="z.B. 1001"
                />
              </div>
              <div className="space-y-2">
                <Label>Ersteller</Label>
                <Input
                  value={currentProject.ersteller || ''}
                  onChange={(e) => handleChange('ersteller', e.target.value)}
                  placeholder="Name des Erstellers"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Erstellt am</Label>
                <Input
                  value={new Date(currentProject.createdAt).toLocaleDateString('de-DE')}
                  disabled
                  className="bg-gray-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Zuletzt bearbeitet</Label>
                <Input
                  value={new Date(currentProject.updatedAt).toLocaleDateString('de-DE')}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Auftraggeber
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={currentProject.kundenName || ''}
                  onChange={(e) => handleChange('kundenName', e.target.value)}
                  placeholder="Kundenname"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={currentProject.kundenTelefon || ''}
                  onChange={(e) => handleChange('kundenTelefon', e.target.value)}
                  placeholder="Telefonnummer"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>E-Mail</Label>
              <Input
                type="email"
                value={currentProject.kundenEmail || ''}
                onChange={(e) => handleChange('kundenEmail', e.target.value)}
                placeholder="E-Mail-Adresse"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" />
              Verteiler-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Hersteller */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hersteller</Label>
                <Select
                  value={currentProject.verteilerHersteller || ''}
                  onValueChange={(value) => handleChange('verteilerHersteller', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Hersteller wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hager">Hager</SelectItem>
                    <SelectItem value="abb">ABB</SelectItem>
                    <SelectItem value="spelsberg">Spelsberg</SelectItem>
                    <SelectItem value="siemens">Siemens</SelectItem>
                    <SelectItem value="eaton">Eaton</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  {currentProject.verteilerHersteller === 'sonstiges' 
                    ? 'Hersteller (Name)' 
                    : 'Verteilerbezeichnung'}
                </Label>
                <Input
                  value={currentProject.verteilerHersteller === 'sonstiges' 
                    ? (currentProject.verteilerHerstellerSonstiges || '')
                    : (currentProject.verteilerBezeichnung || '')}
                  onChange={(e) => handleChange(
                    currentProject.verteilerHersteller === 'sonstiges'
                      ? 'verteilerHerstellerSonstiges'
                      : 'verteilerBezeichnung',
                    e.target.value
                  )}
                  placeholder={currentProject.verteilerHersteller === 'sonstiges'
                    ? 'Herstellername eingeben'
                    : 'z.B. Volta VU36NC'}
                />
              </div>
            </div>

            {/* Verteilertyp und TE pro Reihe */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Verteilertyp</Label>
                <Select
                  value={currentProject.verteilerTyp || ''}
                  onValueChange={(value) => handleChange('verteilerTyp', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Typ wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unterputz">Unterputz (UP)</SelectItem>
                    <SelectItem value="aufputz">Aufputz (AP)</SelectItem>
                    <SelectItem value="hohlwand">Hohlwand (HW)</SelectItem>
                    <SelectItem value="standverteiler">Standverteiler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Teilungseinheiten pro Reihe</Label>
                <Select
                  value={currentProject.teProReihe || '12'}
                  onValueChange={(value) => handleChange('teProReihe', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="TE wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 TE</SelectItem>
                    <SelectItem value="14">14 TE</SelectItem>
                    <SelectItem value="18">18 TE</SelectItem>
                    <SelectItem value="24">24 TE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Felder und Reihen */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Anzahl Felder</Label>
                <Select
                  value={currentProject.anzahlFelder || '1'}
                  onValueChange={(value) => handleChange('anzahlFelder', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Felder wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Feld (einfeldrig)</SelectItem>
                    <SelectItem value="2">2 Felder (zweifeldrig)</SelectItem>
                    <SelectItem value="3">3 Felder (dreifeldrig)</SelectItem>
                    <SelectItem value="4">4 Felder (vierfeldrig)</SelectItem>
                    <SelectItem value="5">5 Felder</SelectItem>
                    <SelectItem value="6">6 Felder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anzahl Reihen (pro Feld)</Label>
                <Select
                  value={currentProject.anzahlReihen || '3'}
                  onValueChange={(value) => handleChange('anzahlReihen', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Reihen wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((num) => (
                      <SelectItem key={num} value={String(num)}>
                        {num} {num === 1 ? 'Reihe' : 'Reihen'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Berechnete Gesamtkapazität */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-sm text-emerald-700">
                <strong>Gesamtkapazität:</strong>{' '}
                {(parseInt(currentProject.anzahlFelder || 1) * 
                  parseInt(currentProject.anzahlReihen || 3) * 
                  parseInt(currentProject.teProReihe || 12))} TE
                <span className="text-emerald-600 ml-2">
                  ({currentProject.anzahlFelder || 1} Feld(er) × {currentProject.anzahlReihen || 3} Reihe(n) × {currentProject.teProReihe || 12} TE)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notizen</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={currentProject.notizen || ''}
              onChange={(e) => handleChange('notizen', e.target.value)}
              placeholder="Projektnotizen..."
              rows={5}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectSettings;
